import { fromProfile16 } from "../charging-profiles";
import type { ChargePointSimulator, EvseState } from "../simulator";
import { certificateHashData, DIAGNOSTICS_STATUSES, fakeCsr, LOG_STATUSES, sameHash, startFirmwareUpdate, startUpload } from "./shared";

type Handlers = Record<string, (params: any) => Promise<any>>;

/** Answers every CSMS-initiated OCPP 1.6J message (all feature profiles + Security extension). */
export const createV16Handlers = (sim: ChargePointSimulator): Handlers => {
  const connector = (id: number | undefined): EvseState | undefined => (id === undefined ? undefined : sim.evse(id));
  const firstFree = () => sim.evses.find(e => !e.transaction && e.operative && e.status !== "Faulted" && !e.reservation);

  const trigger = async (requestedMessage: string, connectorId?: number) => {
    const target = connector(connectorId);
    switch (requestedMessage) {
      case "BootNotification":
        return sim.boot();
      case "Heartbeat":
        return sim.heartbeat();
      case "StatusNotification":
        return target ? sim.sendStatus(target) : sim.sendAllStatuses();
      case "MeterValues":
        for (const evse of target ? [target] : sim.evses) await sim.sendMeterValues(evse.id, "Trigger");
        return;
      case "DiagnosticsStatusNotification":
        return sim.call("DiagnosticsStatusNotification", { status: sim.uploading ? "Uploading" : "Idle" });
      case "FirmwareStatusNotification":
        return sim.call("FirmwareStatusNotification", { status: sim.records.firmwareStatus ?? "Idle" });
      case "LogStatusNotification":
        return sim.call("LogStatusNotification", { status: sim.uploading ? "Uploading" : "Idle" });
      case "SignChargePointCertificate":
        return sim.call("SignCertificate", { csr: fakeCsr(sim.settings.chargePointId) });
      default:
        return undefined;
    }
  };

  const setAvailability = async (evse: EvseState, operative: boolean) => {
    if (evse.transaction && !operative) {
      evse.pendingInoperative = true;
      return "Scheduled";
    }
    evse.operative = operative;
    evse.pendingInoperative = false;
    evse.status = operative ? (evse.reservation ? "Reserved" : "Available") : "Unavailable";
    sim.emitEvse(evse);
    sim.later(() => sim.sendStatus(evse).then(() => undefined));
    return "Accepted";
  };

  return {
    // ---------------------------------------------------------------- Core
    async ChangeAvailability({ connectorId, type }) {
      const operative = type === "Operative";
      const targets = connectorId === 0 ? sim.evses : [connector(connectorId)].filter(Boolean) as EvseState[];
      if (targets.length === 0) return { status: "Rejected" };
      const results = await Promise.all(targets.map(evse => setAvailability(evse, operative)));
      return { status: results.includes("Scheduled") ? "Scheduled" : "Accepted" };
    },

    async ChangeConfiguration({ key, value }) {
      return { status: sim.config.changeConfiguration16(key, value) };
    },

    async ClearCache() {
      sim.records.authorizationCache = {};
      return { status: "Accepted" };
    },

    async DataTransfer({ vendorId, messageId, data }) {
      sim.records.DataTransfer = { vendorId, messageId, data };
      return { status: "Accepted" };
    },

    async GetConfiguration({ key }) {
      return sim.config.getConfiguration16(key);
    },

    async RemoteStartTransaction({ connectorId, idTag, chargingProfile }) {
      const evse = connectorId !== undefined ? connector(connectorId) : firstFree();
      if (!evse || evse.transaction || !evse.operative || evse.status === "Faulted") return { status: "Rejected" };
      if (evse.reservation && evse.reservation.idTag !== idTag) return { status: "Rejected" };
      if (chargingProfile && chargingProfile.chargingProfilePurpose !== "TxProfile") return { status: "Rejected" };
      sim.later(async () => {
        if (sim.config.getBoolean("AuthorizeRemoteTxRequests")) {
          const status = await sim.authorize(idTag);
          if (status !== "Accepted") return;
        }
        await sim.startTransaction(evse.id, {
          idTag,
          ...(chargingProfile && { chargingProfile: fromProfile16(chargingProfile) }),
        });
      });
      return { status: "Accepted" };
    },

    async RemoteStopTransaction({ transactionId }) {
      const evse = sim.evses.find(e => e.transaction?.numericId === transactionId);
      if (!evse) return { status: "Rejected" };
      sim.later(() => sim.stopTransaction(evse.id, "Remote"));
      return { status: "Accepted" };
    },

    async Reset({ type }) {
      const reason = type === "Hard" ? "HardReset" : "SoftReset";
      sim.later(async () => {
        for (const evse of sim.evses) if (evse.transaction) await sim.stopTransaction(evse.id, reason);
        sim.reboot(type);
      });
      return { status: "Accepted" };
    },

    async UnlockConnector({ connectorId }) {
      const evse = connector(connectorId);
      if (!evse) return { status: "NotSupported" };
      // The result is the simulation setting of the connector (Unlocked / UnlockFailed).
      if (evse.transaction) sim.later(() => sim.stopTransaction(evse.id, "UnlockCommand"));
      return { status: evse.unlockResult };
    },

    // ---------------------------------------------------------------- Firmware Management
    async GetDiagnostics({ location, retries, retryInterval, startTime, stopTime }) {
      const { fileName } = startUpload(sim, {
        action: "GetDiagnostics",
        location,
        retries,
        retryInterval,
        from: startTime,
        to: stopTime,
        logType: "DiagnosticsLog",
        statuses: DIAGNOSTICS_STATUSES,
        send: status => sim.call("DiagnosticsStatusNotification", { status }),
      });
      return { fileName };
    },

    async UpdateFirmware({ location, retrieveDate, retries, retryInterval }) {
      startFirmwareUpdate(sim, {
        action: "UpdateFirmware",
        location,
        retrieveAt: retrieveDate,
        retries,
        retryInterval,
        signed: false,
        extendedStatuses: false,
        send: status => sim.call("FirmwareStatusNotification", { status }),
      });
      return {};
    },

    // ---------------------------------------------------------------- Local Auth List Management
    async GetLocalListVersion() {
      return { listVersion: sim.config.getBoolean("LocalAuthListEnabled") ? sim.localList.version : -1 };
    },

    async SendLocalList({ listVersion, localAuthorizationList = [], updateType }) {
      if (!sim.config.getBoolean("LocalAuthListEnabled")) return { status: "NotSupported" };
      if (updateType === "Differential" && listVersion <= sim.localList.version) return { status: "VersionMismatch" };
      if (updateType === "Full") sim.localList.entries.clear();
      for (const entry of localAuthorizationList) {
        if (entry.idTagInfo) sim.localList.entries.set(entry.idTag, { idTagInfo: entry.idTagInfo });
        else sim.localList.entries.delete(entry.idTag);
      }
      sim.localList.version = listVersion;
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Reservation
    async ReserveNow({ connectorId, expiryDate, idTag, reservationId }) {
      // Reserving connector 0 (any connector) is not supported (ReserveConnectorZeroSupported=false).
      if (connectorId === 0) return { status: "Rejected" };
      const evse = connector(connectorId);
      if (!evse) return { status: "Rejected" };
      if (evse.status === "Faulted") return { status: "Faulted" };
      if (!evse.operative) return { status: "Unavailable" };
      const reservedByOther = evse.reservation && evse.reservation.id !== reservationId;
      if (evse.transaction || reservedByOther || (evse.status !== "Available" && evse.status !== "Reserved")) {
        return { status: "Occupied" };
      }
      sim.reserve(evse, { id: reservationId, idTag, expiresAt: expiryDate });
      sim.later(() => sim.sendStatus(evse).then(() => undefined));
      return { status: "Accepted" };
    },

    async CancelReservation({ reservationId }) {
      const evse = sim.evses.find(e => e.reservation?.id === reservationId);
      if (!evse) return { status: "Rejected" };
      sim.clearReservation(evse, true);
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Smart Charging
    async SetChargingProfile({ connectorId, csChargingProfiles }) {
      const profile = fromProfile16(csChargingProfiles);
      const maxStack = sim.config.getNumber("ChargeProfileMaxStackLevel", 8);
      if (profile.stackLevel > maxStack) return { status: "Rejected" };
      if (profile.chargingProfilePurpose === "ChargingStationMaxProfile" && connectorId !== 0) return { status: "Rejected" };
      if (profile.chargingProfilePurpose === "TxProfile") {
        const evse = connector(connectorId);
        if (!evse?.transaction) return { status: "Rejected" };
        if (csChargingProfiles.transactionId !== undefined && csChargingProfiles.transactionId !== evse.transaction.numericId) {
          return { status: "Rejected" };
        }
        profile.transactionId = evse.transaction.id;
      } else if (connectorId !== 0 && !connector(connectorId)) {
        return { status: "Rejected" };
      }
      sim.profiles.install(connectorId, profile);
      return { status: "Accepted" };
    },

    async ClearChargingProfile({ id, connectorId, chargingProfilePurpose, stackLevel }) {
      const purpose =
        chargingProfilePurpose === "ChargePointMaxProfile" ? "ChargingStationMaxProfile" : chargingProfilePurpose;
      const removed = sim.profiles.clear({ id, evseId: connectorId, purpose, stackLevel });
      return { status: removed > 0 ? "Accepted" : "Unknown" };
    },

    async GetCompositeSchedule({ connectorId, duration, chargingRateUnit }) {
      if (connectorId !== 0 && !connector(connectorId)) return { status: "Rejected" };
      const tx = connector(connectorId)?.transaction;
      const schedule = sim.profiles.composite(connectorId, duration, chargingRateUnit, tx?.id);
      if (!schedule) return { status: "Rejected" };
      return {
        status: "Accepted",
        connectorId,
        scheduleStart: sim.timestamp(),
        chargingSchedule: {
          duration: schedule.duration,
          startSchedule: sim.timestamp(),
          chargingRateUnit: schedule.chargingRateUnit,
          chargingSchedulePeriod: schedule.chargingSchedulePeriod,
        },
      };
    },

    // ---------------------------------------------------------------- Remote Trigger
    async TriggerMessage({ requestedMessage, connectorId }) {
      if (connectorId !== undefined && connectorId !== 0 && !connector(connectorId)) return { status: "Rejected" };
      sim.later(() => trigger(requestedMessage, connectorId).then(() => undefined));
      return { status: "Accepted" };
    },

    // ---------------------------------------------------------------- Security extension
    async CertificateSigned({ certificateChain }) {
      if (!certificateChain.includes("BEGIN CERTIFICATE")) return { status: "Rejected" };
      sim.certificates.push({
        certificateType: "ChargePointCertificate",
        certificate: certificateChain,
        hashData: certificateHashData(certificateChain),
      });
      return { status: "Accepted" };
    },

    async InstallCertificate({ certificateType, certificate }) {
      if (!certificate.includes("BEGIN CERTIFICATE")) return { status: "Rejected" };
      sim.certificates.push({ certificateType, certificate, hashData: certificateHashData(certificate) });
      return { status: "Accepted" };
    },

    async DeleteCertificate({ certificateHashData: hash }) {
      const index = sim.certificates.findIndex(c => sameHash(c.hashData, hash));
      if (index < 0) return { status: "NotFound" };
      sim.certificates.splice(index, 1);
      return { status: "Accepted" };
    },

    async GetInstalledCertificateIds({ certificateType }) {
      const found = sim.certificates.filter(c => c.certificateType === certificateType).map(c => c.hashData);
      return found.length > 0 ? { status: "Accepted", certificateHashData: found } : { status: "NotFound" };
    },

    async ExtendedTriggerMessage({ requestedMessage, connectorId }) {
      if (connectorId !== undefined && connectorId !== 0 && !connector(connectorId)) return { status: "Rejected" };
      sim.later(() => trigger(requestedMessage, connectorId).then(() => undefined));
      return { status: "Accepted" };
    },

    async GetLog({ logType, requestId, retries, retryInterval, log }) {
      const { fileName, cancelledPrevious } = startUpload(sim, {
        action: "GetLog",
        location: log.remoteLocation,
        retries,
        retryInterval,
        from: log.oldestTimestamp,
        to: log.latestTimestamp,
        logType,
        statuses: LOG_STATUSES,
        send: status => sim.call("LogStatusNotification", { status, requestId }),
      });
      return { status: cancelledPrevious ? "AcceptedCanceled" : "Accepted", filename: fileName };
    },

    async SignedUpdateFirmware({ requestId, retries, retryInterval, firmware }) {
      if (!firmware.signingCertificate.includes("BEGIN CERTIFICATE")) return { status: "InvalidCertificate" };
      const { cancelledPrevious } = startFirmwareUpdate(sim, {
        action: "SignedUpdateFirmware",
        location: firmware.location,
        retrieveAt: firmware.retrieveDateTime,
        installAt: firmware.installDateTime,
        retries,
        retryInterval,
        signed: true,
        extendedStatuses: true,
        send: status => sim.call("SignedFirmwareStatusNotification", { status, requestId }),
      });
      return { status: cancelledPrevious ? "AcceptedCanceled" : "Accepted" };
    },
  };
};
